import { qk, useMenu, useSettings, useSpecials } from '@restaurant/api-client/react';
import { formatMoney, type MenuCategoryDto, type MenuItemDto } from '@restaurant/shared';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ImagePlus, Pencil, Plus, Sparkles, Star, Trash2, UtensilsCrossed } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import { EmptyState, PageHeader, Select, Skeleton, Spinner, Switch } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toastError } from '@/lib/errors';
import { cn, todayYmd } from '@/lib/utils';
import { SpecialsCard, useSpecialDrafts } from './SpecialsCard';
import { DietTagsRow } from './DietTags';

export function MenuPage() {
  const { data: menu, isLoading } = useMenu();
  const { data: settings } = useSettings();
  const { user } = useAuth();
  const qc = useQueryClient();
  const isOwner = user?.role === 'owner';
  const [editing, setEditing] = useState<{ item?: MenuItemDto; categoryId?: string } | null>(null);
  const [catName, setCatName] = useState('');
  const refresh = () => qc.invalidateQueries({ queryKey: qk.menu });
  const currency = settings?.currency ?? 'LKR';
  const [specialsDate, setSpecialsDate] = useState(() => todayYmd(settings?.timezone));
  const { data: savedSpecials } = useSpecials(specialsDate);
  const [specialDrafts, setSpecialDrafts] = useSpecialDrafts(savedSpecials, specialsDate);
  const itemsById = useMemo(() => new Map((menu ?? []).flatMap((c) => c.items).map((i) => [i.id, i])), [menu]);
  const isSpecial = (id: string) => specialDrafts.some((d) => d.menuItemId === id);
  const toggleSpecial = (id: string) =>
    setSpecialDrafts(isSpecial(id) ? specialDrafts.filter((d) => d.menuItemId !== id) : [...specialDrafts, { menuItemId: id, price: '', note: '' }]);

  const [reviewOnly, setReviewOnly] = useState(false);
  const [tagging, setTagging] = useState(false);
  const allItems = useMemo(() => (menu ?? []).flatMap((c) => c.items), [menu]);
  const needReview = allItems.filter((i) => !i.dietTagsReviewed).length;
  const untagged = allItems.filter((i) => !i.dietTags.length).length;
  const autoTag = async () => {
    setTagging(true);
    try {
      const r = await api.menu.autoTag(untagged === 0);
      toast.success(r.queued ? `AI is tagging ${r.pending} dishes. This takes about a minute; refresh to see them.` : 'Nothing to tag');
      window.setTimeout(() => void refresh(), 45_000);
    } catch (err) {
      toastError(err);
    } finally {
      setTagging(false);
    }
  };

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    try {
      await fn();
      if (ok) toast.success(ok);
      await refresh();
    } catch (err) {
      toastError(err);
    }
  };

  const move = (list: { id: string }[], idx: number, dir: -1 | 1, save: (ids: string[]) => Promise<void>) => {
    const ids = list.map((x) => x.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j]!, ids[idx]!];
    void run(() => save(ids));
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Menu"
        description={isOwner ? 'Items, prices and aliases (AI uses aliases to match what customers type).' : 'Mark items sold out during service.'}
        actions={
          isOwner ? (
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (catName.trim()) void run(() => api.menu.createCategory({ name: catName.trim() }), 'Category added').then(() => setCatName(''));
              }}
            >
              <Input value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="New category" className="w-40" />
              <Button type="submit" variant="outline"><Plus /> Category</Button>
            </form>
          ) : null
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
        {isOwner && allItems.length ? (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border bg-card px-4 py-2.5 text-sm">
            <span>
              <b>Diet tags</b> keep recommendations safe (e.g. no meat for vegetarians).{' '}
              {needReview ? <span className="text-muted-foreground">{needReview} dish{needReview === 1 ? '' : 'es'} need{needReview === 1 ? 's' : ''} review{untagged ? `, ${untagged} untagged` : ''}.</span> : <span className="text-success">All reviewed.</span>}
            </span>
            <div className="ml-auto flex items-center gap-3">
              {needReview ? (
                <label className="flex items-center gap-2 text-xs"><Switch checked={reviewOnly} onCheckedChange={setReviewOnly} /> Needs review only</label>
              ) : null}
              {needReview ? (
                <Button size="sm" variant="outline" onClick={() => void autoTag()} disabled={tagging}>{tagging ? <Spinner /> : <Sparkles />} {untagged ? 'Auto-tag with AI' : 'Re-check unreviewed with AI'}</Button>
              ) : null}
            </div>
          </div>
        ) : null}
        {isLoading ? (
          <div className="space-y-4">{[0, 1].map((i) => <Skeleton key={i} className="h-40" />)}</div>
        ) : !menu?.length ? (
          <EmptyState icon={<UtensilsCrossed />} title="Your menu is empty" description="Add a category, then add items with prices." />
        ) : (
          <div className="mx-auto max-w-4xl space-y-6">
            <SpecialsCard
              date={specialsDate}
              onDateChange={setSpecialsDate}
              drafts={specialDrafts}
              setDrafts={setSpecialDrafts}
              saved={savedSpecials ?? []}
              items={itemsById}
              currency={currency}
              canEdit={isOwner}
            />
            {menu.map((cat: MenuCategoryDto, ci) => (
              <Card key={cat.id} className="overflow-hidden">
                <div className="flex items-center justify-between gap-2 border-b bg-muted/40 px-4 py-2.5">
                  <h2 className="font-semibold">{cat.name} <span className="text-sm font-normal text-muted-foreground">· {cat.items.length}</span></h2>
                  {isOwner ? (
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" aria-label="Move category up" onClick={() => move(menu, ci, -1, api.menu.reorderCategories)}><ArrowUp /></Button>
                      <Button size="icon" variant="ghost" aria-label="Move category down" onClick={() => move(menu, ci, 1, api.menu.reorderCategories)}><ArrowDown /></Button>
                      <Button size="icon" variant="ghost" aria-label="Delete category" onClick={() => void run(() => api.menu.deleteCategory(cat.id), 'Category removed')}><Trash2 /></Button>
                      <Button size="sm" onClick={() => setEditing({ categoryId: cat.id })}><Plus /> Item</Button>
                    </div>
                  ) : null}
                </div>
                <ul>
                  {cat.items.filter((item) => !reviewOnly || !item.dietTagsReviewed).map((item, ii) => (
                    <li key={item.id} className="flex items-center gap-3 border-b px-4 py-2.5 last:border-0">
                      {item.imageUrl ? <img src={item.imageUrl} alt="" className="size-10 rounded-md object-cover" /> : <div className="size-10 rounded-md bg-muted" />}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {item.name} {!item.available ? <Badge variant="destructive" className="ml-1">Sold out</Badge> : null}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {item.aliases.length ? `aka ${item.aliases.join(', ')}` : item.description ?? ''}
                          {item.options.length ? ` · ${item.options.length} option(s)` : ''}
                        </p>
                        <DietTagsRow item={item} canEdit={isOwner} onSaved={() => void refresh()} />
                      </div>
                      <span className="text-sm tabular-nums">{formatMoney(item.priceCents, currency)}</span>
                      <label className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Switch checked={item.available} onCheckedChange={(v) => void run(() => api.menu.setAvailability(item.id, v), v ? `${item.name} available` : `${item.name} marked sold out`)} aria-label="Available" />
                      </label>
                      {isOwner ? (
                        <div className="flex">
                          <Button size="icon" variant="ghost" aria-label={isSpecial(item.id) ? 'Remove from specials' : 'Mark as special'} onClick={() => toggleSpecial(item.id)}>
                            <Star className={cn(isSpecial(item.id) && 'fill-amber-400 text-amber-500')} />
                          </Button>
                          <Button size="icon" variant="ghost" aria-label="Move up" onClick={() => move(cat.items, ii, -1, api.menu.reorderItems)}><ArrowUp /></Button>
                          <Button size="icon" variant="ghost" aria-label="Move down" onClick={() => move(cat.items, ii, 1, api.menu.reorderItems)}><ArrowDown /></Button>
                          <Button size="icon" variant="ghost" aria-label="Edit" onClick={() => setEditing({ item })}><Pencil /></Button>
                        </div>
                      ) : null}
                    </li>
                  ))}
                  {cat.items.length === 0 ? <li className="px-4 py-3 text-sm text-muted-foreground">No items in this category.</li> : null}
                </ul>
              </Card>
            ))}
          </div>
        )}
      </div>
      {editing && menu ? (
        <ItemDialog
          categories={menu}
          item={editing.item}
          categoryId={editing.categoryId}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function ItemDialog({ categories, item, categoryId, onClose, onSaved }: { categories: MenuCategoryDto[]; item?: MenuItemDto; categoryId?: string; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    categoryId: item?.categoryId ?? categoryId ?? categories[0]!.id,
    name: item?.name ?? '',
    description: item?.description ?? '',
    price: item ? String(item.priceCents / 100) : '',
    imageUrl: item?.imageUrl ?? '',
    aliases: item?.aliases.join(', ') ?? '',
    available: item?.available ?? true,
    options: item?.options.map((o) => ({ id: o.id as string | undefined, name: o.name, price: String(o.priceDeltaCents / 100) })) ?? [],
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const input = {
        categoryId: f.categoryId,
        name: f.name.trim(),
        description: f.description.trim() || null,
        priceCents: Math.round(Number(f.price) * 100),
        imageUrl: f.imageUrl || null,
        available: f.available,
        aliases: f.aliases.split(',').map((a) => a.trim()).filter(Boolean),
        options: f.options.filter((o) => o.name.trim()).map((o) => ({ ...(o.id ? { id: o.id } : {}), name: o.name.trim(), priceDeltaCents: Math.round(Number(o.price || 0) * 100) })),
      };
      if (item) await api.menu.updateItem(item.id, input);
      else await api.menu.createItem(input);
      toast.success('Item saved');
      onSaved();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const { url } = await api.menu.uploadImage(file, file.name);
      setF((x) => ({ ...x, imageUrl: url }));
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={item ? `Edit ${item.name}` : 'New item'} side="right">
        <div className="grid gap-4">
          <Field label="Category"><Select value={f.categoryId} onValueChange={(v) => setF({ ...f, categoryId: v })} options={categories.map((c) => ({ value: c.id, label: c.name }))} /></Field>
          <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
          <Field label="Price" hint="In rupees, e.g. 1450"><Input inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value.replace(/[^\d.]/g, '') })} /></Field>
          <Field label="Aliases" hint="Comma-separated names customers use, e.g. koththu, chicken kottu"><Input value={f.aliases} onChange={(e) => setF({ ...f, aliases: e.target.value })} /></Field>
          <Field label="Description"><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <div className="flex items-center gap-3">
            {f.imageUrl ? <img src={f.imageUrl} alt="" className="size-16 rounded-md object-cover" /> : <div className="size-16 rounded-md bg-muted" />}
            <label className="inline-flex h-8 cursor-pointer items-center gap-2 rounded-md border px-3 text-xs font-medium hover:bg-accent">
              {uploading ? <Spinner /> : <ImagePlus className="size-4" />} Upload image
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])} />
            </label>
            {f.imageUrl ? <Button size="sm" variant="ghost" onClick={() => setF({ ...f, imageUrl: '' })}>Remove</Button> : null}
          </div>
          <div className="grid gap-2">
            <p className="text-sm font-medium">Options</p>
            {f.options.map((o, i) => (
              <div key={i} className="flex gap-2">
                <Input value={o.name} placeholder="e.g. Extra cheese" onChange={(e) => setF({ ...f, options: f.options.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                <Input className="w-28" inputMode="decimal" value={o.price} placeholder="+ price" onChange={(e) => setF({ ...f, options: f.options.map((x, j) => (j === i ? { ...x, price: e.target.value.replace(/[^\d.]/g, '') } : x)) })} />
                <Button size="icon" variant="ghost" aria-label="Remove option" onClick={() => setF({ ...f, options: f.options.filter((_, j) => j !== i) })}><Trash2 /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="justify-self-start" onClick={() => setF({ ...f, options: [...f.options, { id: undefined, name: '', price: '' }] })}><Plus /> Add option</Button>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.available} onCheckedChange={(v) => setF({ ...f, available: v })} /> Available</label>
          <div className="flex justify-between gap-2">
            {item ? (
              <Button variant="ghost" className="text-destructive" onClick={() => void api.menu.archiveItem(item.id).then(onSaved).catch(toastError)}><Trash2 /> Delete</Button>
            ) : <span />}
            <Button onClick={() => void save()} disabled={busy || !f.name.trim() || !f.price}>{busy ? <Spinner /> : null} Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
